import { cx, text } from '../../../lib/ui-primitives'
import { formatMYR } from '../../../lib/utils'

// ============================================
// STATS GRID
// Dashboard statistics cards
// ============================================

export interface DashboardStats {
  total: number
  pending: number
  approved: number
  rejected: number
  todayCount: number
  todayVolume: number
}

export interface StatsGridProps {
  stats: DashboardStats
}

// Stat card config
interface StatCardConfig {
  label: string
  value: number | string
  color?: 'default' | 'warning' | 'success' | 'info'
  formatAsCurrency?: boolean
}

const colorStyles = {
  default: 'bg-white/5 border-white/10 text-white',
  warning: 'bg-amber-500/10 border-amber-500/30 text-amber-400',
  success: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
  info: 'bg-indigo-500/10 border-indigo-500/30 text-indigo-400',
}

/**
 * StatsGrid - Dashboard statistics overview
 *
 * Displays key metrics in a responsive grid.
 */
export function StatsGrid({ stats }: StatsGridProps) {
  const cards: StatCardConfig[] = [
    { label: 'Total Orders', value: stats.total, color: 'default' },
    { label: 'Pending', value: stats.pending, color: 'warning' },
    { label: 'Approved', value: stats.approved, color: 'success' },
    { label: "Today's Volume", value: stats.todayVolume, color: 'info', formatAsCurrency: true },
  ]

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
      {cards.map((card) => (
        <StatCard key={card.label} {...card} />
      ))}
    </div>
  )
}

function StatCard({ label, value, color = 'default', formatAsCurrency }: StatCardConfig) {
  const displayValue = formatAsCurrency ? formatMYR(value as number) : value

  return (
    <div
      className={cx(
        'rounded-xl p-3 sm:p-4 border',
        colorStyles[color]
      )}
    >
      <div className={cx('text-xs sm:text-sm mb-1', color === 'default' ? text.tertiary : '')}>
        {label}
      </div>
      <div className={cx('text-xl sm:text-2xl font-bold', text.numeric)}>
        {displayValue}
      </div>
    </div>
  )
}
